/**
 * Layout Shell — App → Root → FlatRoutes → pages
 *
 * Root renders the SidebarPage wrapper. The Sidebar contains the logo,
 * nav groups (Menu, Settings), and MyGroupsSidebarItem. FlatRoutes (in
 * App.tsx) maps URL paths to page components — Revenium marketplace pages
 * and Backstage built-ins alike.
 *
 * CSS-separation convention (enforced from Phase 1):
 *   - Sidebar appearance (hover, selected) → themes/revenium.ts styleOverrides
 *   - Layout dimensions → makeStyles((theme) => ({...})) in this file
 *   - No inline style= props, no hardcoded hex, no raw px magic numbers
 *
 * See docs/architecture/frontend-component-tree.md for the full tree and
 * docs/architecture/frontend-styling-conventions.md for the styling rules.
 */
import React, { PropsWithChildren } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import StorefrontIcon from '@material-ui/icons/Storefront';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import CategoryIcon from '@material-ui/icons/Category';

// NOTE: Cannot be expressed as a BackstageSidebarDivider theme override because
// SidebarDivider is a styled("hr") component, not makeStyles — theme.overrides
// does not reach styled() components in MUI v4 (only makeStyles/withStyles).
// The fix must live here, reading the groupSeparatorColor palette token directly.
const useSidebarStyles = makeStyles((theme: Theme) => ({
  divider: {
    // Full-width hairline separator reading the groupSeparatorColor palette token.
    background: (theme.palette as any).navigation?.groupSeparatorColor
      ?? 'rgba(255, 255, 255, 0.10)',
    width: '100%',
    height: '1px',
    border: 'none',
    margin: `${theme.spacing(1.5)}px 0`,
    display: 'block',
  },
}));

const useSidebarLogoStyles = makeStyles((theme: Theme) => ({
  // Proper header-space container for the logo: full width, centered, with
  // generous top/bottom padding so the seal is never clipped (the previous
  // fixed `height: 3 * logoHeight` with no padding cut off the top of the
  // 112px image on hover/expand). Height is driven by content + padding, not
  // a magic multiple — so the image always fits.
  root: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Phase-4 DOM inspection (VIS-02 / D-10, live Docker DevTools, 2026-07-01)
    // confirmed the logo container centers correctly: the SidebarLogo root div
    // computes to width:100%, display:flex, justify-content:center — there is NO
    // offending Backstage Sidebar wrapper ancestor forcing a left/right offset.
    // Any residual "slightly right-of-center" perception is internal whitespace
    // in the seal PNG itself, not a DOM/CSS layout bug; compensating for it in
    // layout CSS would be the exact blind tweak D-10 prohibits. VIS-02 is
    // therefore resolved-by-inspection with no code override.
  },
  // White-line separator beneath the logo header — same token as the group
  // separators, so the logo reads as a distinct header band.
  separator: {
    background: (theme.palette as any).navigation?.groupSeparatorColor
      ?? 'rgba(255, 255, 255, 0.10)',
    width: '100%',
    height: '1px',
    border: 'none',
    margin: 0,
    display: 'block',
  },
}));

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <>
      <div className={classes.root}>
        <Link to="/" underline="none" className={classes.link} aria-label="Home">
          {isOpen ? <LogoFull /> : <LogoIcon />}
        </Link>
      </div>
      <hr className={classes.separator} />
    </>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const sidebarClasses = useSidebarStyles();
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        {/*<SidebarGroup label="Search" icon={<SearchIcon />} to="/search">*/}
        {/*  <SidebarSearchModal />*/}
        {/*</SidebarGroup>*/}
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          {/* ENT-79: Marketplace lives at /marketplace but had no nav entry —
              this is its reachable sidebar item. */}
          <SidebarItem icon={StorefrontIcon} to="/marketplace" text="Marketplace" />
          {/*<SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />*/}

          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          {/*<SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />*/}
          {/* End global nav */}
          <hr className={sidebarClasses.divider} />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <hr className={sidebarClasses.divider} />
        <MyGroupsSidebarItem
            singularTitle="My Group"
            pluralTitle="My Groups"
            icon={GroupIcon}
        />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
